"use client";

import { Dialog as Base } from "@base-ui/react/dialog";
import { createContext, forwardRef, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import type { HTMLAttributes } from "react";
import { withoutProps, buttonHandlers, allowDismiss, focusCallback, slotted, useSurfaceEvents } from "./shared";
import type { ButtonProps, PortalProps, RootProps, SurfaceEvents, SurfaceProps } from "./shared";

const Context = createContext<{ open: boolean; handlers: { current: SurfaceEvents }; setMotionPortal: (forceMount: boolean) => void; releaseMotionPortal: (forceMount: boolean) => void; actions: { current: Base.Root.Actions | null } } | null>(null);

export function Root({ open: controlled, defaultOpen = false, onOpenChange, ...props }: RootProps) {
  const [internal, setInternal] = useState(defaultOpen);
  const open = controlled ?? internal;
  const handlers = useRef<SurfaceEvents>({});
  const motionPortal = useRef(false);
  const actions = useRef<Base.Root.Actions | null>(null);
  const previousOpen = useRef(open), syncingExternalClose = useRef(false);
  const latestOpen = useRef(open);
  useLayoutEffect(() => { latestOpen.current = open; }, [open]);
  const setMotionPortal = useCallback((forceMount: boolean) => { motionPortal.current = forceMount; }, []);
  const releaseMotionPortal = useCallback((forceMount: boolean) => { motionPortal.current = false; if (forceMount && !latestOpen.current) actions.current?.unmount(); }, []);
  useLayoutEffect(() => {
    if (previousOpen.current && !open && motionPortal.current) {
      syncingExternalClose.current = true;
      try { actions.current?.close(); } finally { syncingExternalClose.current = false; }
    }
    previousOpen.current = open;
  }, [open]);
  return <Context.Provider value={{ open, handlers, setMotionPortal, releaseMotionPortal, actions }}><Base.Root {...props} open={open} actionsRef={actions} onOpenChange={(next, details) => {
    if (!next && !allowDismiss(details.reason, details.event, handlers.current)) { details.cancel(); return; }
    if (!next && motionPortal.current) details.preventUnmountOnClose();
    if (syncingExternalClose.current) return;
    if (controlled === undefined) setInternal(next);
    onOpenChange?.(next);
  }} /></Context.Provider>;
}

export const Trigger = forwardRef<HTMLButtonElement, ButtonProps>(function Trigger({ asChild, children, ...props }, ref) {
  const context = useContext(Context);
  return <Base.Trigger {...props} {...buttonHandlers(props)} ref={ref} data-state={context?.open ? "open" : "closed"} {...slotted(asChild, children)} />;
});
export const Close = forwardRef<HTMLButtonElement, ButtonProps>(function Close({ asChild, children, ...props }, ref) {
  return <Base.Close {...props} {...buttonHandlers(props)} ref={ref} {...slotted(asChild, children)} />;
});
export function Portal({ forceMount, ...props }: PortalProps) {
  const context = useContext(Context);
  const setMotionPortal = context?.setMotionPortal, releaseMotionPortal = context?.releaseMotionPortal;
  useLayoutEffect(() => {
    setMotionPortal?.(!!forceMount);
    return () => releaseMotionPortal?.(!!forceMount);
  }, [forceMount, setMotionPortal, releaseMotionPortal]);
  return <Base.Portal {...props} keepMounted={forceMount} />;
}
export const Overlay = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { asChild?: boolean; forceMount?: boolean }>(function Overlay({ asChild, forceMount, children, ...props }, ref) {
  const context = useContext(Context);
  return <Base.Backdrop {...props} forceRender={forceMount} ref={ref} data-state={context ? context.open ? "open" : "closed" : undefined} {...slotted(asChild, children)} />;
});
export const Content = forwardRef<HTMLDivElement, SurfaceProps>(function Content({ asChild, children, initialFocus, finalFocus, onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown, onPointerDownOutside, onFocusOutside, onInteractOutside, ...props }, ref) {
  const context = useContext(Context);
  const fallback = useRef<SurfaceEvents>({});
  useSurfaceEvents(context?.handlers ?? fallback, { onEscapeKeyDown, onPointerDownOutside, onFocusOutside, onInteractOutside });
  return <Base.Popup {...withoutProps(props, ["forceMount"])} ref={ref} data-state={context ? context.open ? "open" : "closed" : undefined} initialFocus={focusCallback(onOpenAutoFocus, initialFocus)} finalFocus={focusCallback(onCloseAutoFocus, finalFocus)} {...slotted(asChild, children)} />;
});
export const Title = Base.Title;
export const Description = Base.Description;
